import { Building2 } from 'lucide-react';
import { useParams, useSearchParams } from 'react-router';

import { useUsuario } from '@/lib/auth/contexto';
import { podeNaEmpresa } from '@/lib/auth/permissoes';
import { mensagemDeErro } from '@/lib/erros';
import { DocumentoTexto } from '@/ui/components/documento-texto';
import { EstadoVazio } from '@/ui/components/estado-vazio';
import { CabecalhoPagina } from '@/ui/patterns/cabecalho-pagina';
import { DetalheEmAbas } from '@/ui/patterns/detalhe-em-abas';
import { Badge } from '@/ui/primitives/badge';
import { Skeleton } from '@/ui/primitives/skeleton';
import { Migalhas } from '@/ui/shell/migalhas';
import { type EmpresaDetalhe } from '@fiscoai/contracts';

import { AbaCaptura } from './aba-captura';
import { AbaCertificado } from './aba-certificado';
import { AbaContador } from './aba-contador';
import { AbaEndereco } from './aba-endereco';
import { AbaIdentificacao } from './aba-identificacao';
import { AbaIntegracoes } from './aba-integracoes';
import { AbaMembros } from './aba-membros';
import { AbaParametros } from './aba-parametros';
import { AbaTributario } from './aba-tributario';
import { useEmpresa } from './api';
import { AbaCentrosCusto } from './centros-custo/aba-centros-custo';
import { AbaClassificacoes } from './classificacoes/aba-classificacoes';
import { AtualizarPeloCartao } from './dialogo-atualizar-cartao';
import { AbaRegras } from './regras/aba-regras';
import { PENDENCIA, REGIME } from './rotulos';

function Resumo({ empresa, podeEditar }: { empresa: EmpresaDetalhe; podeEditar: boolean }) {
  return (
    <CabecalhoPagina
      acoes={podeEditar && <AtualizarPeloCartao empresa={empresa} />}
      titulo={empresa.nomeFantasia ?? empresa.razaoSocial}
      descricao={
        <span className="flex flex-wrap items-center gap-2">
          <DocumentoTexto valor={empresa.cnpj} />
          {empresa.regimeVigente && <Badge tom="marca">{REGIME[empresa.regimeVigente]}</Badge>}
          {!empresa.ativa && <Badge tom="perigo">Inativa</Badge>}
          {empresa.pendencias.map((p) => (
            <Badge key={p} tom={PENDENCIA[p].tom}>
              {PENDENCIA[p].rotulo}
            </Badge>
          ))}
        </span>
      }
    />
  );
}

export function PaginaEmpresa() {
  const { id = '' } = useParams();
  const usuario = useUsuario();
  const [params, setParams] = useSearchParams();
  const consulta = useEmpresa(id);
  const empresa = consulta.data;

  if (consulta.isError)
    return (
      <EstadoVazio
        tom="perigo"
        icone={<Building2 />}
        titulo="Empresa não encontrada"
        texto={mensagemDeErro(consulta.error)}
      />
    );
  if (!empresa) return <Skeleton className="h-96 rounded-painel" />;

  const editar = podeNaEmpresa(usuario, empresa.id, 'empresa.cadastro.editar');
  const membros = podeNaEmpresa(usuario, empresa.id, 'empresa.membros.gerenciar');
  const certificado = podeNaEmpresa(usuario, empresa.id, 'empresa.certificado.gerenciar');
  const captura = podeNaEmpresa(usuario, empresa.id, 'empresa.captura.gerenciar');
  const p = { empresa, podeEditar: editar };
  return (
    <div className="grid gap-4">
      <Migalhas
        itens={[
          { rotulo: 'Empresas', para: '/empresas' },
          { rotulo: empresa.nomeFantasia ?? empresa.razaoSocial },
        ]}
      />
      <DetalheEmAbas
        rotulo="Cadastro da empresa"
        resumo={<Resumo empresa={empresa} podeEditar={editar} />}
        aba={params.get('aba') ?? 'identificacao'}
        aoMudarAba={(aba) => {
          setParams({ aba }, { replace: true });
        }}
        abas={[
          {
            valor: 'identificacao',
            rotulo: 'Identificação',
            conteudo: <AbaIdentificacao {...p} />,
          },
          { valor: 'endereco', rotulo: 'Endereço', conteudo: <AbaEndereco {...p} /> },
          { valor: 'tributario', rotulo: 'Tributário', conteudo: <AbaTributario {...p} /> },
          { valor: 'parametros', rotulo: 'Parâmetros fiscais', conteudo: <AbaParametros {...p} /> },
          {
            valor: 'regras',
            rotulo: 'Regras de classificação',
            conteudo: <AbaRegras {...p} />,
          },
          {
            valor: 'classificacoes',
            rotulo: 'Classificações',
            conteudo: <AbaClassificacoes {...p} />,
          },
          {
            valor: 'centros-custo',
            rotulo: 'Centros de custo',
            conteudo: <AbaCentrosCusto {...p} />,
          },
          { valor: 'contador', rotulo: 'Contador', conteudo: <AbaContador {...p} /> },
          {
            valor: 'certificado',
            rotulo: 'Certificado',
            conteudo: <AbaCertificado empresa={empresa} podeGerenciar={certificado} />,
          },
          {
            valor: 'captura',
            rotulo: 'Captura SEFAZ',
            conteudo: (
              <AbaCaptura
                empresa={empresa}
                podeGerenciar={captura}
                podeEnviarCertificado={certificado}
              />
            ),
          },
          { valor: 'integracoes', rotulo: 'Integrações', conteudo: <AbaIntegracoes {...p} /> },
          ...(membros
            ? [{ valor: 'membros', rotulo: 'Membros', conteudo: <AbaMembros empresa={empresa} /> }]
            : []),
        ]}
      />
    </div>
  );
}
